import React, { useState, useRef, useEffect } from 'react';
import styles from './ChatContainer.module.scss';
import { ChatInput } from '../ChatInput/ChatInput';
import { ChatMessage } from '../ChatMessage/ChatMessage';
import {
  IChatContainer,
  IGetMessagesResponse
} from './ChatContainer.interface';
import {
  ask,
  deleteChatMessages,
  getChatMessages,
  sendFeedback
} from 'integration';
import {
  useAppSelector,
  useAppDispatch,
  getSelectedDocuments,
  setSelectedDocuments,
  setCanSelectDocuments,
  getBusinessArea,
  getService
} from 'app/redux';
import { LoadingSpinner } from 'app/components';
import { SERVICE_NAMES } from 'app/utils/consts';
import { IFeedback } from 'app/utils';

interface ILocalMessage {
  id: string;
  message: string;
  timestamp?: string;
  type: string;
}

export const ChatContainer: React.FC<IChatContainer> = ({
  chatId,
  model,
  isNewChat,
  onFirstMessage
}) => {
  const [messages, setMessages] = useState<ILocalMessage[]>([]);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useAppDispatch();
  const selectedDocuments = useAppSelector(getSelectedDocuments);
  const [isLoadingMessages, setIsLoadingMessages] = useState<boolean>(false);
  const currentService =
    useAppSelector(getService) ?? SERVICE_NAMES.ASK_YOUR_DOCS;
  const currentBusinessArea =
    useAppSelector(getBusinessArea) ?? 'cross-functional';

  const logError = (context: string, error: unknown): void => {
    // eslint-disable-next-line no-console
    console.error(`${context}:`, error);
  };

  const handleGetMessages = async (chatId: string): Promise<void> => {
    setIsLoadingMessages(true);
    try {
      const response: IGetMessagesResponse = await getChatMessages(
        currentBusinessArea,
        currentService,
        chatId
      );
      const now = Date.now();
      const mapped = response.messages.map((msg, idx) => ({
        ...msg,
        id: msg.timestamp ?? `srv-${idx}-${now}`
      }));
      setMessages(mapped);
      dispatch(setSelectedDocuments(response.attachedDocs));
      dispatch(setCanSelectDocuments(false));
    } catch (error) {
      logError('Error loading chat messages', error);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  useEffect(() => {
    let isActive = true;
    const run = async () => {
      if (chatId && !isNewChat) {
        await handleGetMessages(chatId);
      } else if (isNewChat || !chatId) {
        if (isActive) setMessages([]);
      }
    };
    run();
    return () => {
      isActive = false;
    };
  }, [chatId, isNewChat]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const sendMessage = async (
    text: string,
    chosenModel: 'fast' | 'smart'
  ): Promise<void> => {
    if (!text.trim()) return;
    const now = Date.now();
    const userMessage: ILocalMessage = {
      id: `usr-${now}`,
      type: 'question',
      message: text
    };
    const loadingMessage: ILocalMessage = {
      id: `loading-${now}`,
      type: 'answer',
      message: 'loading'
    };
    setMessages((prevMessages) => [
      ...prevMessages,
      userMessage,
      loadingMessage
    ]);

    const payload = {
      chatId,
      question: text,
      attachedDocs: selectedDocuments,
      isNewChatBool: isNewChat,
      chosenModel
    };

    try {
      const response = await ask(currentBusinessArea, currentService, payload);
      const botMessage: ILocalMessage = {
        id: `ans-${Date.now()}`,
        type: 'answer',
        message: response.answer
      };
      setMessages((prevMessages) => {
        const updatedMessages = prevMessages.slice(0, -1);
        const lastIdx = updatedMessages.length - 1;
        const lastMsg = updatedMessages[lastIdx];
        if (lastMsg?.type === 'question') {
          updatedMessages[lastIdx] = {
            ...lastMsg,
            timestamp: response.question_timestamp
          };
        }
        return [...updatedMessages, botMessage];
      });
      if (isNewChat) {
        dispatch(setCanSelectDocuments(false));
        onFirstMessage();
      }
    } catch (error) {
      logError('Error sending message', error);
      setMessages((prevMessages) => {
        const withoutLast = prevMessages.slice(0, -1);
        const errorMessage: ILocalMessage = {
          id: `err-${Date.now()}`,
          type: 'answer',
          message: 'There was an error processing your request.'
        };
        return [...withoutLast, errorMessage];
      });
    }
  };

  const handleDeleteMessage = async (timestamp: string): Promise<void> => {
    if (!chatId) return;
    try {
      await deleteChatMessages(
        currentBusinessArea,
        currentService,
        chatId,
        timestamp
      );
      setMessages((prevMessages) => {
        const targetIndex = prevMessages.findIndex(
          (msg) => msg.timestamp === timestamp
        );
        if (targetIndex === -1) return prevMessages;
        return prevMessages.filter((_, idx) => idx < targetIndex);
      });
    } catch (error) {
      logError('Error deleting message', error);
    }
  };

  const handleFeedback = (feedback: string, message: string): void => {
    const feedbackData: IFeedback = {
      origin: `Feedback for message: "${message.substring(0, 20)}${
        message.length > 20 ? '...' : ''
      }"`,
      businessArea: currentBusinessArea,
      businessService: currentService,
      feedback: `${feedback}`
    };

    sendFeedback(feedbackData).catch((error) => {
      logError('Error sending feedback', error);
    });
  };

  const getDisabledState = (): { disabled: boolean; reason: string } => {
    if (!chatId) {
      return {
        disabled: true,
        reason: 'Select or create a new chat to start messaging'
      };
    }
    if (selectedDocuments.length === 0) {
      return {
        disabled: true,
        reason: 'Select at least one document to ask questions about'
      };
    }
    return { disabled: false, reason: '' };
  };
  const { disabled, reason } = getDisabledState();

  return (
    <div className={styles['chat-container']}>
      <div className={styles.messages}>
        {messages.map((message, index) => (
          <ChatMessage
            key={message.id}
            type={message.type}
            message={message.message}
            timestamp={message.timestamp}
            onDelete={handleDeleteMessage}
            canDelete={message.type === 'question' && index > 0}
            onFeedback={handleFeedback}
            showFeedback={
              message.type === 'answer' && message.message !== 'loading'
            }
          />
        ))}
        <div ref={messagesEndRef}></div>
      </div>

      <ChatInput
        onSendMessage={sendMessage}
        model={model}
        disabled={disabled}
        disabledMessage={reason}
      />
      {isLoadingMessages && <LoadingSpinner />}
    </div>
  );
};
